import { CaseTableApi, type CaseTableRow } from "@/api/caseTable/CaseTableApi";
import {
  DEFAULT_CASE_TABLE_DEFINITION,
  type CaseTableDefinition,
} from "@/api/caseTable/caseTableDefinition";
import { queryKeys } from "@/api/queryKeys";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useCaseTable(definition: CaseTableDefinition) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.caseTable.list(JSON.stringify(definition)),
    queryFn: () => CaseTableApi.list(definition),
    enabled: !!user?.id,
  });
  return {
    rows: (q.data?.rows ?? []) as CaseTableRow[],
    hasMore: !!q.data?.has_more,
    nextCursor: q.data?.next_cursor ?? null,
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useSavedCaseListViews() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.caseTable.savedViews,
    queryFn: () => CaseTableApi.listSavedViews(),
    enabled: !!user?.id,
  });
  return { views: q.data ?? [], loading: q.isLoading, refetch: q.refetch };
}

export function useToggleCaseBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => CaseTableApi.toggleBookmark(caseId),
    onSuccess: (_data, caseId) => {
      void qc.invalidateQueries({ queryKey: ["caseTable"] });
      void qc.invalidateQueries({ queryKey: queryKeys.caseTable.bookmark(caseId) });
    },
  });
}

export function useCaseBookmark(caseId: string | undefined) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.caseTable.bookmark(caseId ?? ""),
    queryFn: () => CaseTableApi.isBookmarked(caseId!),
    enabled: !!user?.id && !!caseId,
  });
  return { bookmarked: !!q.data, loading: q.isLoading, refetch: q.refetch };
}

export { DEFAULT_CASE_TABLE_DEFINITION };
